import React, { useState } from 'react';
import { createRateQuery, enviaGenerateOrder } from '../cart/Utils';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const CarrierDetails = ({
  selectedCarrier,
  carrierDetails,
  loadingDetails,
  onCardClick,
  orderDetails,
  onOrderPlaced,
}) => {
  const [rateData, setRateData] = useState({});
  const [loadingRate, setLoadingRate] = useState({});
  const [expanded, setExpanded] = useState({});
  const [selectedCard, setSelectedCard] = useState(null);
  const [orderStatus, setOrderStatus] = useState({});
  const [orderData, setOrderData] = useState({});
  const [errorMessage, setErrorMessage] = useState({});

  if (!selectedCarrier) return null;

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCardClick = async (detail) => {
    const key = `${detail.carrier_name}-${detail.name}`;
    setSelectedCard(key);
    setLoadingRate((prev) => ({ ...prev, [key]: true }));
    setOrderStatus((prev) => ({ ...prev, [key]: null }));
    setErrorMessage((prev) => ({ ...prev, [key]: null }));

    try {
      const rateQuery = await createRateQuery(
        orderDetails,
        orderDetails.shippingAddress,
        detail.carrier_name,
        detail.name,
        orderDetails.items
      );

      if (rateQuery.success) {
        const rateInfo = rateQuery.data.find(
          (rate) =>
            rate.carrier === detail.carrier_name.toLowerCase() &&
            rate.service === detail.name.toLowerCase()
        );
        setRateData((prev) => ({ ...prev, [key]: rateInfo }));
      } else {
        setRateData((prev) => ({ ...prev, [key]: { error: rateQuery.error } }));
      }
    } catch (error) {
      setRateData((prev) => ({ ...prev, [key]: { error: error.message } }));
    } finally {
      setLoadingRate((prev) => ({ ...prev, [key]: false }));
    }

    if (onCardClick) {
      onCardClick(detail);
    }
  };

  const handlePriceButtonClick = async (detail) => {
    const key = `${detail.carrier_name}-${detail.name}`;
    setOrderStatus((prev) => ({ ...prev, [key]: 'loading' }));
    setErrorMessage((prev) => ({ ...prev, [key]: null }));

    try {
      const response = await enviaGenerateOrder(
        orderDetails,
        orderDetails.shippingAddress,
        detail.carrier_name,
        detail.name,
        orderDetails.items
      );
      console.log("envia order response", response);

      if (response.success && response.data) {
        const orderInfo = Array.isArray(response.data) ? response.data : [response.data];
        setOrderData((prev) => ({ ...prev, [key]: orderInfo }));
        setOrderStatus((prev) => ({ ...prev, [key]: 'success' }));

        // Map all shipments to tracking info
        const orderTrackingInfos = orderInfo.map((shipment) => ({
          trackingNumber: shipment.trackingNumber || shipment.id,
          trackUrl: shipment.trackingUrl,
          shipmentId: shipment.shipmentId,
        }));

        console.log('Order Tracking Information:', orderTrackingInfos);

        if (onOrderPlaced) {
          onOrderPlaced(orderTrackingInfos);
        }
      } else {
        const errorMsg = response.error || 'Failed to place order';
        setOrderStatus((prev) => ({ ...prev, [key]: 'error' }));
        setErrorMessage((prev) => ({ ...prev, [key]: errorMsg }));
      }
    } catch (err) {
      const fallbackMessage = err.message || 'Unexpected failure during order placement';
      setOrderStatus((prev) => ({ ...prev, [key]: 'error' }));
      setErrorMessage((prev) => ({ ...prev, [key]: fallbackMessage }));
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-xl font-semibold text-[#DBAE6E] mb-4">Carrier Details</h3>

      {loadingDetails ? (
        <p className="text-sm text-gray-500 italic">Loading...</p>
      ) : carrierDetails.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {carrierDetails.map((detail, index) => {
            const key = `${detail.carrier_name}-${detail.name}`;
            const rateInfo = rateData[key];
            const isLoading = loadingRate[key];
            const isExpanded = expanded[key];
            const isSelected = selectedCard === key;
            const status = orderStatus[key];
            const errorMsg = errorMessage[key];

            return (
              <div
                key={index}
                className={`bg-[#333] p-4 rounded-md shadow-md hover:bg-[#444] transition-all cursor-pointer relative ${
                  isSelected ? 'border-4 border-[#DBAE6E]' : ''
                }`}
                onClick={() => handleCardClick(detail)}
              >
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-md z-10">
                    <div className="w-8 h-8 border-4 border-t-[#DBAE6E] border-gray-300 rounded-full animate-spin"></div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {detail.logo && (
                      <img
                        src={detail.logo}
                        alt={`${detail.carrier_name} logo`}
                        className="w-12 h-12 object-contain mr-4"
                      />
                    )}
                    <div>
                      <h4 className="text-lg font-semibold text-[#DBAE6E]">
                        {detail.carrier_name} - {detail.name}
                      </h4>
                      <p className="text-sm text-gray-300">{detail.type || 'Standard'}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(key);
                    }}
                    className="text-[#DBAE6E] hover:text-[#E8C78F] focus:outline-none"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                </div>

                {isExpanded && (
                  <div className="text-sm text-gray-200 mt-4">
                    <p>
                      <strong className="text-[#DBAE6E]">Service:</strong> {detail.name || '-'}
                    </p>
                    <p>
                      <strong className="text-[#DBAE6E]">Delivery Estimate:</strong>{' '}
                      {detail.delivery_estimate || '-'}
                    </p>
                    <p>
                      <strong className="text-[#DBAE6E]">Cash on Delivery:</strong>{' '}
                      {detail.cash_on_delivery ? 'Yes' : 'No'}
                    </p>
                    {rateInfo?.error && (
                      <p className="text-red-500">
                        <strong className="text-[#DBAE6E]">Error:</strong> {rateInfo.error}
                      </p>
                    )}
                  </div>
                )}

                {isSelected && rateInfo && !rateInfo.error && (
                  <div className="mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePriceButtonClick(detail);
                      }}
                      disabled={status === 'loading' || status === 'success'}
                      className={`w-full py-2 rounded-md text-white text-lg font-bold transition-all ${
                        status === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : status === 'error'
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-[#DBAE6E] hover:bg-[#E8C78F]'
                      } flex items-center justify-center gap-2`}
                    >
                      {status === 'loading' ? (
                        <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : status === 'success' ? (
                        <>
                          <FaCheckCircle />
                          Order Placed
                        </>
                      ) : status === 'error' ? (
                        <>
                          <FaTimesCircle />
                          Try Another One
                        </>
                      ) : (
                        `₹${rateInfo.totalPrice.toFixed(2)}`
                      )}
                    </button>
                    {status === 'error' && errorMsg && (
                      <p className="text-red-500 text-sm mt-2 text-center">Error: {errorMsg}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center">No carrier details available.</p>
      )}
    </div>
  );
};

export default CarrierDetails;